import { accountApi, ApiError, type AccountDTO } from '../net/accountApi';
import { loadToken, saveToken, clearToken } from './accountStore';

type Listener = (a: AccountDTO | null) => void;

class AccountState {
  private current: AccountDTO | null = null;
  private listeners = new Set<Listener>();

  get(): AccountDTO | null {
    return this.current;
  }

  isAuthenticated(): boolean {
    return this.current != null && loadToken() != null;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.current);
    return () => this.listeners.delete(fn);
  }

  set(account: AccountDTO | null): void {
    this.current = account;
    for (const fn of this.listeners) fn(account);
  }

  /** Try to load the account from a stored token. Returns null if no token,
   *  or if the token is invalid (in which case the local token is cleared). */
  async loadFromStorage(): Promise<AccountDTO | null> {
    if (!loadToken()) return null;
    try {
      const { account } = await accountApi.me();
      this.set(account);
      return account;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
        clearToken();
        this.set(null);
        return null;
      }
      // Network or transient error — keep token, return null but don't clear.
      console.warn('[account] /me failed', e);
      return null;
    }
  }

  async register(name: string): Promise<AccountDTO> {
    const { token, account } = await accountApi.register(name);
    saveToken(token);
    this.set(account);
    return account;
  }

  async rename(name: string): Promise<AccountDTO> {
    const { account } = await accountApi.rename(name);
    this.set(account);
    return account;
  }

  async rotateToken(): Promise<{ token: string; account: AccountDTO }> {
    const { token, account } = await accountApi.rotateToken();
    saveToken(token);
    this.set(account);
    return { token, account };
  }

  async importToken(token: string): Promise<AccountDTO> {
    const { account } = await accountApi.importToken(token);
    saveToken(token);
    this.set(account);
    return account;
  }

  async refresh(): Promise<AccountDTO | null> {
    return this.loadFromStorage();
  }
}

export const accountState = new AccountState();
export type { AccountDTO } from '../net/accountApi';
